import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useCurrentUserProfile } from '../contexts/CurrentUserProfileContext';
import { useFollowWrite } from '../hooks/mutations/useFollowWrite';
import { useFollowingForUser } from '../hooks/queries/usePeopleQueries';
import { openAppSettings, readContacts } from '../lib/native/priiPeople';
import { hashContacts, normalPhone } from '../lib/people/contactHashes';
import { priiCodeUrl } from '../lib/people/priiCode';
import { shareLink } from '../lib/shareLink';
import { peopleListSurfaceClass } from '../config/glassSurfaces';
import PageBackHeader from './ui/PageBackHeader';
import Button from './ui/Button';
import SearchField from './search/SearchField';
import Avatar from './Avatar';
import PeopleConnectionRow from './people/PeopleConnectionRow';
import './people/People.css';

const INVITE_ROWS = 60;

async function findContactsOnPrii() {
  try {
    const r = await readContacts();
    if (r.denied || r.unavailable) return { status: r.denied ? 'denied' : 'unavailable' };
    const contacts = r.contacts || [];
    const { hashes, byHash } = await hashContacts(contacts);
    const calls = [];
    for (let i = 0; i < hashes.length; i += 5000)
      calls.push(supabase.rpc('match_contacts', { p_hashes: hashes.slice(i, i + 5000) }));
    const matches = [];
    for (const { data, error } of await Promise.all(calls)) {
      if (error) throw error;
      matches.push(...(data || []));
    }
    const onPrii = new Set();
    const people = matches.map((m) => {
      const i = byHash.get(m.matched_hash);
      onPrii.add(i);
      return {
        id: m.id,
        display_name: m.display_name,
        profile_photo_url: m.profile_photo_url,
        contactName: contacts[i]?.name || '',
      };
    });
    const invite = contacts
      .map((c, i) => ({ ...c, i, phone: (c.phones || []).map(normalPhone).find(Boolean) }))
      .filter((c) => !onPrii.has(c.i) && c.name && c.phone)
      .sort((x, y) => x.name.localeCompare(y.name));
    return { status: 'ready', people, invite };
  } catch (err) {
    console.error('[Contacts] match failed:', err);
    return { status: 'failed' };
  }
}

/**
 * Contacts (Stage 5, Sam's 4C): who in your address book is already on Prii, with search beside Follow all (laid out
 * like Notifications' Mark all read), then the rest with Invite. Only SHA-256 hashes of emails and numbers leave the
 * phone (migration 167 matches them).
 */
export default function ContactsPage() {
  const navigate = useNavigate();
  const { session, profile } = useCurrentUserProfile();
  const me = session?.user?.id ?? null;
  const { data: following = [] } = useFollowingForUser(me, { enabled: Boolean(me), limit: 1000 });
  const followingIds = useMemo(() => new Set(following.map((p) => p.id)), [following]);
  const { follow } = useFollowWrite();
  const [query, setQuery] = useState('');
  // Kept for the session, so coming back is instant; a fresh check after 10 minutes
  const { data: state = { status: 'loading' } } = useQuery({
    queryKey: ['contacts-match', me],
    enabled: Boolean(me),
    staleTime: 10 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    retry: false,
    queryFn: findContactsOnPrii,
  });

  const q = query.trim().toLowerCase();
  const hit = (...names) =>
    !q ||
    names.some((n) =>
      String(n || '')
        .toLowerCase()
        .includes(q),
    );
  const people = (state.people || []).filter((p) => hit(p.display_name, p.contactName));
  const invite = (state.invite || []).filter((c) => hit(c.name));
  const notFollowed = people.filter((p) => !followingIds.has(p.id));
  const link = me ? priiCodeUrl(me, profile?.display_name) : null;

  const sendInvite = (c) => {
    const body = `Join me on Prii ${link}`;
    // Messages, straight to them; the share sheet if the phone can't open sms: (the web)
    if (c.phone && /iPhone|iPad/.test(navigator.userAgent))
      window.location.href = `sms:${c.phone}&body=${encodeURIComponent(body)}`;
    else shareLink({ url: link, text: 'Join me on Prii' });
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-6">
      <PageBackHeader
        title="Friends already on Prii"
        onBack={() => navigate(-1)}
        className="!mb-1"
      />
      <p className="text-sm text-dark-muted">
        From your contacts. Numbers are matched privately and never shown.
      </p>

      {state.status === 'unavailable' && (
        <p className="mt-8 text-center text-sm text-dark-muted">Contacts work in the Prii app.</p>
      )}
      {state.status === 'failed' && (
        <p className="mt-8 text-center text-sm text-dark-muted">
          Couldn't check your contacts. Try again with signal.
        </p>
      )}
      {state.status === 'denied' && (
        <div className="mt-8 text-center">
          <b className="block text-dark-text">Contacts are off for Prii</b>
          <p className="mt-1 text-sm text-dark-muted">
            Turn them on in Settings to see who's here.
          </p>
          <Button className="mt-3" onClick={openAppSettings}>
            Open Settings
          </Button>
        </div>
      )}

      {(state.status === 'ready' || state.status === 'loading') && (
        <>
          <div className="mt-4 flex items-center gap-2">
            <div className="flex-[2] min-w-0">
              <SearchField
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onClear={() => setQuery('')}
                placeholder="Search contacts"
              />
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="shrink-0 whitespace-nowrap"
              disabled={notFollowed.length === 0}
              onClick={() => notFollowed.forEach((p) => follow(p))}
            >
              {people.length > 0 && notFollowed.length === 0 ? 'Following all' : 'Follow all'}
            </Button>
          </div>
          {state.status === 'loading' && (
            <div className="mt-3" aria-label="Finding your friends">
              <p className="mb-2 text-sm text-dark-muted">Finding your friends…</p>
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3 px-2 py-2.5 animate-pulse">
                  <div className="h-9 w-9 shrink-0 rounded-full skeleton-shimmer" />
                  <div className="h-3.5 w-2/5 rounded skeleton-shimmer" />
                </div>
              ))}
            </div>
          )}
          {state.status === 'ready' && (
            <div className="mt-2">
              {people.length === 0 ? (
                <p className="py-6 text-center text-sm text-dark-muted">
                  {q ? 'No one on Prii by that name.' : 'None of your contacts are on Prii yet.'}
                </p>
              ) : (
                <div className={peopleListSurfaceClass()}>
                  {people.map((p) => (
                    <PeopleConnectionRow
                      key={p.id}
                      profile={p}
                      subtitle={
                        p.contactName && p.contactName !== p.display_name
                          ? p.contactName
                          : undefined
                      }
                      isFollowing={followingIds.has(p.id)}
                      onFollow={() => follow(p)}
                      onNavigate={() => navigate(`/profile/${p.id}`)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {invite.length > 0 && (
            <>
              <p className="pp-label mt-6">Not on Prii yet</p>
              {invite.slice(0, INVITE_ROWS).map((c) => (
                <div key={c.i} className="flex items-center gap-3 px-2 py-1.5">
                  <Avatar displayName={c.name} size="xs" />
                  <span className="min-w-0 flex-1 truncate text-sm text-dark-text">{c.name}</span>
                  <Button variant="secondary" size="sm" onClick={() => sendInvite(c)}>
                    Invite
                  </Button>
                </div>
              ))}
              {invite.length > INVITE_ROWS && (
                <p className="mt-2 text-center text-xs text-dark-muted">Search to find the rest.</p>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
